import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useDriveUpload } from '@/user_files/drive/hooks/useDriveUpload'
import { selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import { isTerminalProcessingStatus, isWaitingForOcrQuota } from '@/user_files/utils/processing-status'
import { useUserFilesPollingProcessor } from '@/user_files/polling/hooks/useUserFilesPollingProcessor'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { ConflictModal } from '@/user_files/drive/components/ConflictModal'
import {
  selectAgentConversationFormAttachedPendingUploads,
  selectAgentConversationFormUserFileIds,
  selectAgentConversationFormWritingSampleFileIds,
} from '@/agent-v2/chat-form/store/selectors'
import { getAcceptedFilesList } from '@/constants/constants-gcs'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { getOrCreateUserFilesUploadFolderId } from '@/user_files/folders/utils/get-or-create-user-files-upload-folder-id'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import type { FileSelectionTarget } from '@/agent-v2/chat-form/store/schemas'
import { getOcrQuota } from '@/ocr_quota/fetch/get-ocr-quota'
import type { OcrQuotaSummary } from '@/ocr_quota/schemas'
import { requiresOcrForUpload } from '@/ocr_quota/utils/requires-ocr-for-upload'
import { OcrUploadBlockedModal } from '@/ocr_quota/components/OcrUploadBlockedModal'
import { useOcrQuotaWaiterRefresh } from '@/ocr_quota/hooks/useOcrQuotaWaiterRefresh'

const ASSISTANT_UPLOADS_FOLDER_NAME = 'Assistant Uploads'
// Chat shows no usage summary, so refreshing a waiter only re-reads its file.
const refreshNoQuotaSummary = async () => undefined

type ChatWindowUserFilesUploadProps = {
  conversationId: string
  matterId: string | null
  /** What an uploaded file attaches to. Defaults to the multi-file user_files (RAG) behavior. */
  uploadTarget?: FileSelectionTarget
  onFileInitialized?: (sourceFile: File, serverFileId: string) => void
  onSubmissionBlockedReason?: (reason: string | null) => void
}

/** Whether a file passed to `addFilesAndAwaitRegistration` got an upload row. */
export type ChatWindowUserFilesUploadRegistration =
  | { sourceFile: File; registered: true; clientTmpId: string }
  | { sourceFile: File; registered: false; reason: string }

export interface ChatWindowUserFilesUploadRef {
  openSystemFileSelector: () => void
  addFiles: (files: File[]) => void
  /**
   * Adds files like `addFiles`, and resolves once each one has an upload row or was discarded.
   * A registered file is then followed by its `clientTmpId` in upload state; registration alone
   * does not mean the file uploaded or finished processing. Not for folder drops.
   */
  addFilesAndAwaitRegistration: (files: File[]) => Promise<ChatWindowUserFilesUploadRegistration[]>
  cancelUpload: (clientTmpId: string) => void
}

type UploadFolderFailure = 'uploads-unavailable' | 'folder-not-prepared'

type RegistrationWaiter = {
  /** Set once the file reaches the drive upload hook, which then either registers or discards it. */
  forwardedTick: number | null
  settle: (registration: ChatWindowUserFilesUploadRegistration) => void
}

const ChatWindowUserFilesUpload = forwardRef<ChatWindowUserFilesUploadRef, ChatWindowUserFilesUploadProps>(function ChatWindowUserFilesUpload(
  { conversationId, matterId, uploadTarget = 'user-files', onFileInitialized, onSubmissionBlockedReason },
  ref
) {
  const isWritingSample = uploadTarget === 'writing-sample'
  const { enabled: emailFileProcessingEnabled } = useRuntimeFeatureFlag(RuntimeFeatureFlag.EMAIL_FILE_PROCESSING)
  const acceptedFileTypes = Object.values(getAcceptedFilesList(emailFileProcessingEnabled)).flat().join(',')
  const sourceFilesRef = useRef(new Map<string, File>())
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const attachedFileIds = useAppSelector((state) =>
    isWritingSample
      ? selectAgentConversationFormWritingSampleFileIds(state, { conversationId })
      : selectAgentConversationFormUserFileIds(state, { conversationId })
  )
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)
  const [uploadFolderId, setUploadFolderId] = useState<string | null>(null)
  const uploadFolderIdRef = useRef<string | null>(null)
  const currentMatterIdRef = useRef<string | null>(matterId)
  const filesWaitingForUploadFolderRef = useRef<File[] | null>(null)
  const openPickerAfterFolderReadyRef = useRef(false)
  const [preparingFolder, setPreparingFolder] = useState(false)
  const [preparationError, setPreparationError] = useState<string | null>(null)
  const [blockedQuota, setBlockedQuota] = useState<OcrQuotaSummary | null>(null)
  const registrationWaitersRef = useRef(new Map<File, RegistrationWaiter>())
  const forwardTickRef = useRef(0)
  const [forwardTick, setForwardTick] = useState(0)

  const failRegistrationWaiters = useCallback((reason: string, files?: File[]) => {
    for (const sourceFile of files ?? [...registrationWaitersRef.current.keys()]) {
      registrationWaitersRef.current.get(sourceFile)?.settle({ sourceFile, registered: false, reason })
    }
  }, [])

  const markFilesForwarded = useCallback((files: File[]) => {
    const waiters = files.map((file) => registrationWaitersRef.current.get(file)).filter((waiter) => waiter !== undefined)
    if (waiters.length === 0) return
    forwardTickRef.current += 1
    for (const waiter of waiters) waiter.forwardedTick = forwardTickRef.current
    setForwardTick(forwardTickRef.current)
  }, [])

  const fileIdsNeedingPolling = useMemo(
    () =>
      attachedFileIds.filter((fileId) => {
        const uploadFile = uploadFilesByServerFileId[fileId]
        if (!uploadFile) return false
        if (uploadFile.client.uploadStatus !== 'uploaded') return false
        return !isTerminalProcessingStatus(uploadFile.backend?.record.processing_status)
      }),
    [uploadFilesByServerFileId, attachedFileIds]
  )

  useUserFilesPollingProcessor({
    sourceId: `assistant-${uploadTarget}-upload-${conversationId}`,
    fileIds: fileIdsNeedingPolling,
    enabled: fileIdsNeedingPolling.length > 0,
  })

  // The shared poller skips files waiting for OCR quota, so these refresh at the slower waiter
  // cadence; once pages free up, the attachment stops blocking send without leaving the chat.
  const quotaWaitingFileIds = useMemo(
    () => fileIdsNeedingPolling.filter((fileId) => isWaitingForOcrQuota(uploadFilesByServerFileId[fileId]?.backend?.record)),
    [fileIdsNeedingPolling, uploadFilesByServerFileId]
  )
  useOcrQuotaWaiterRefresh({ fileIds: quotaWaitingFileIds, refreshQuota: refreshNoQuotaSummary })

  const {
    isPreparingFiles,
    openUploadPicker,
    disabled: driveUploadDisabled,
    fileInputRef,
    addFiles,
    cancelUpload,
    conflictDialog,
  } = useDriveUpload({
    folderId: uploadFolderId,
    onFileRegistered: ({ clientTmpId, sourceFile }) => {
      registrationWaitersRef.current.get(sourceFile)?.settle({ sourceFile, registered: true, clientTmpId })
      if (onFileInitialized) sourceFilesRef.current.set(clientTmpId, sourceFile)
      dispatch(AgentConversationFormActions.addPendingUserFileUpload({ conversationId, clientTmpId, target: uploadTarget }))
    },
    onFileInitialized: ({ clientTmpId, serverFileId }) => {
      const sourceFile = sourceFilesRef.current.get(clientTmpId)
      if (sourceFile) {
        onFileInitialized?.(sourceFile, serverFileId)
        sourceFilesRef.current.delete(clientTmpId)
      }
      const attachedPendingUploads = selectAgentConversationFormAttachedPendingUploads(store.getState(), { conversationId })
      if (!attachedPendingUploads.some((upload) => upload.clientTmpId === clientTmpId)) return

      dispatch(
        isWritingSample
          ? AgentConversationFormActions.addWritingSampleFile({ conversationId, userFileId: serverFileId })
          : AgentConversationFormActions.addUserFile({ conversationId, userFileId: serverFileId })
      )
      dispatch(AgentConversationFormActions.removePendingUserFileUpload({ conversationId, clientTmpId }))
    },
    onUploadCompleted: ({ folderId: completedFolderId }) => {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: completedFolderId }))
    },
    onUploadFailed: ({ folderId: failedFolderId }) => {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: failedFolderId }))
    },
  })

  const disabled = !matterId || (Boolean(uploadFolderId) && driveUploadDisabled)

  useEffect(() => {
    onSubmissionBlockedReason?.(preparationError ?? (preparingFolder || isPreparingFiles ? 'Finish preparing attachments before sending.' : null))
  }, [onSubmissionBlockedReason, preparationError, preparingFolder, isPreparingFiles])

  useEffect(() => () => onSubmissionBlockedReason?.(null), [onSubmissionBlockedReason])

  useEffect(() => () => failRegistrationWaiters('The upload was closed before this file was queued.'), [failRegistrationWaiters])

  // The drive upload hook registers a file as soon as it queues it. Once that hook has nothing left
  // to prepare, a forwarded file that never registered was discarded (for example a cancelled
  // filename conflict), so its waiter must not stay pending.
  useEffect(() => {
    if (preparingFolder || isPreparingFiles) return
    for (const [sourceFile, waiter] of registrationWaitersRef.current) {
      if (waiter.forwardedTick === null || waiter.forwardedTick > forwardTick) continue
      waiter.settle({ sourceFile, registered: false, reason: 'This file was not added to the upload queue.' })
    }
  }, [forwardTick, isPreparingFiles, preparingFolder])

  const prepareUploadFolder = useCallback(async (): Promise<string | null> => {
    if (!matterId) return null
    if (uploadFolderIdRef.current) return uploadFolderIdRef.current

    const requestedMatterId = matterId
    try {
      const folderId = await getOrCreateUserFilesUploadFolderId({
        dispatch,
        folderName: ASSISTANT_UPLOADS_FOLDER_NAME,
        getState: store.getState,
        matterId: requestedMatterId,
      })
      if (currentMatterIdRef.current !== requestedMatterId) return null

      uploadFolderIdRef.current = folderId
      return folderId
    } catch (error) {
      console.error('Failed to prepare Matter Files upload folder:', error)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'File could not be uploaded.',
          durationMs: kToastDurationMediumMs,
        })
      )
      return null
    }
  }, [dispatch, matterId, store])

  useEffect(() => {
    currentMatterIdRef.current = matterId
    uploadFolderIdRef.current = null
    filesWaitingForUploadFolderRef.current = null
    openPickerAfterFolderReadyRef.current = false
    setUploadFolderId(null)
    setPreparingFolder(false)
    setPreparationError(null)
    setBlockedQuota(null)
    failRegistrationWaiters('The matter changed before this file was queued.')
    return () => {
      currentMatterIdRef.current = null
    }
  }, [failRegistrationWaiters, matterId])

  // Continue the original upload intent after the folder id has reached useDriveUpload.
  useEffect(() => {
    if (!uploadFolderId) return

    if (openPickerAfterFolderReadyRef.current) {
      openPickerAfterFolderReadyRef.current = false
      openUploadPicker()
    }

    const files = filesWaitingForUploadFolderRef.current
    if (!files) return
    filesWaitingForUploadFolderRef.current = null
    addFiles(files)
    markFilesForwarded(files)
    setPreparingFolder(false)
  }, [addFiles, markFilesForwarded, openUploadPicker, uploadFolderId])

  /** Hands the files to the drive upload hook once the upload folder exists; returns why it could not. */
  const addFilesAfterUploadFolderReady = useCallback(
    async (files: File[]): Promise<UploadFolderFailure | null> => {
      if (files.length === 0) return null
      if (disabled) return 'uploads-unavailable'

      setPreparingFolder(true)
      const requestedMatterId = matterId
      let allowedFiles = files
      if (files.some(requiresOcrForUpload)) {
        let rejectedQuota: OcrQuotaSummary | null = null
        try {
          const quota = await getOcrQuota()
          if (quota.enforcement_enabled && quota.is_exhausted) {
            allowedFiles = files.filter((file) => !requiresOcrForUpload(file))
            rejectedQuota = quota
          }
        } catch {
          // Only a confirmed full quota rejects a file here. When the check cannot answer (an older
          // backend, an outage, or an unreadable response), the upload continues and server-side
          // admission queues any file that needs OCR until pages are available.
        }
        if (currentMatterIdRef.current !== requestedMatterId) return null
        if (rejectedQuota) {
          setBlockedQuota(rejectedQuota)
          failRegistrationWaiters('Cannot upload files requiring OCR (.pdf) while your quota is full', files.filter(requiresOcrForUpload))
        }
      }
      if (allowedFiles.length === 0) {
        setPreparingFolder(false)
        return null
      }
      const folderId = await prepareUploadFolder()
      if (!folderId) {
        setPreparingFolder(false)
        return 'folder-not-prepared'
      }

      if (uploadFolderId === folderId) {
        addFiles(allowedFiles)
        markFilesForwarded(allowedFiles)
        setPreparingFolder(false)
        return null
      }

      filesWaitingForUploadFolderRef.current = [...(filesWaitingForUploadFolderRef.current ?? []), ...allowedFiles]
      setUploadFolderId(folderId)
      return null
    },
    [addFiles, disabled, failRegistrationWaiters, markFilesForwarded, matterId, prepareUploadFolder, uploadFolderId]
  )

  const addFilesAndAwaitRegistration = useCallback(
    (files: File[]): Promise<ChatWindowUserFilesUploadRegistration[]> => {
      const registrations = files.map(
        (sourceFile) =>
          new Promise<ChatWindowUserFilesUploadRegistration>((resolve) => {
            registrationWaitersRef.current.set(sourceFile, {
              forwardedTick: null,
              settle: (registration) => {
                registrationWaitersRef.current.delete(sourceFile)
                resolve(registration)
              },
            })
          })
      )
      // Not awaited: a matter change or unmount settles the waiters while the folder request is still pending.
      void addFilesAfterUploadFolderReady(files).then((failure) => {
        if (failure === 'uploads-unavailable') failRegistrationWaiters('Uploads are not available right now.', files)
        if (failure === 'folder-not-prepared') failRegistrationWaiters('The upload folder could not be prepared.', files)
      })
      return Promise.all(registrations)
    },
    [addFilesAfterUploadFolderReady, failRegistrationWaiters]
  )

  const openSystemFileSelectorAfterUploadFolderReady = useCallback(async () => {
    if (disabled) return

    const folderId = await prepareUploadFolder()
    if (!folderId) return

    if (uploadFolderId === folderId) {
      openUploadPicker()
      return
    }

    openPickerAfterFolderReadyRef.current = true
    setUploadFolderId(folderId)
  }, [disabled, openUploadPicker, prepareUploadFolder, uploadFolderId])

  useImperativeHandle(ref, () => ({
    openSystemFileSelector: () => {
      void openSystemFileSelectorAfterUploadFolderReady()
    },
    addFiles: (files) => {
      void addFilesAfterUploadFolderReady(files).then((failure) => {
        if (failure === 'uploads-unavailable') {
          setPreparationError('These attachments were not queued. Remove this failed selection, then retry when uploads are available.')
        }
        if (failure === 'folder-not-prepared') {
          setPreparationError('The attachment destination could not be prepared. Remove this failed selection, then import again.')
        }
      })
    },
    addFilesAndAwaitRegistration,
    cancelUpload: (clientTmpId) => {
      cancelUpload(clientTmpId)
    },
  }))

  return (
    <>
      {onSubmissionBlockedReason && preparationError && (
        <div role="alert">
          {preparationError}
          <button type="button" onClick={() => setPreparationError(null)}>
            Remove failed selection
          </button>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple={!isWritingSample}
        accept={acceptedFileTypes}
        disabled={disabled}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ''
          void addFilesAfterUploadFolderReady(files)
        }}
      />
      <ConflictModal {...conflictDialog} operation="upload" />
      {/* Let accepted files resolve name conflicts before presenting the rejected-file notice. */}
      <OcrUploadBlockedModal
        open={blockedQuota !== null && !preparingFolder && !isPreparingFiles && !conflictDialog.open}
        showUsage={blockedQuota?.is_trial === true}
        onDismiss={() => setBlockedQuota(null)}
      />
    </>
  )
})

export default ChatWindowUserFilesUpload
